import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Copy, GripVertical, MoreVertical, Trash2 } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ToggleMini } from '~/components/toggle-mini';
import { cn } from '@/lib/utils';
import type { ActionType } from '~/lib/schemas/actions';
import { ACTION_TYPE_META, ACTION_TYPE_META_MAP } from './action-type-meta';

interface ActionCardProps {
	index: number;
	actionType: ActionType;
	onTypeChange: (type: ActionType) => void;
	combineWithOthers: boolean;
	onCombineChange: (value: boolean) => void;
	/** dnd-kit listeners/attributes for the drag handle. */
	dragHandleProps?: Record<string, unknown>;
	onRemove: () => void;
	onDuplicate?: () => void;
	children: ReactNode;
}

/**
 * Shared chrome for any promotion action: teal left-edge accent, head (drag
 * handle, sequence badge, type selector, combine toggle, overflow menu) and a
 * type-specific body slot. The 8 action bodies (Phase F) render into `children`.
 */
export function ActionCard({
	index,
	actionType,
	onTypeChange,
	combineWithOthers,
	onCombineChange,
	dragHandleProps,
	onRemove,
	onDuplicate,
	children
}: ActionCardProps) {
	const { t } = useTranslation();
	const meta = ACTION_TYPE_META_MAP[actionType];
	const TypeIcon = meta.icon;

	return (
		<div className="overflow-hidden rounded-lg border border-l-4 border-border border-l-brand bg-surface">
			<div className="flex items-center gap-2 border-b border-border-subtle bg-elevated px-3 py-2">
				<button type="button" {...dragHandleProps} className="cursor-grab text-foreground-subtle hover:text-foreground" aria-label="drag">
					<GripVertical size={16} />
				</button>
				<span className="flex h-6 w-6 items-center justify-center rounded bg-brand text-xs font-semibold text-on-brand">{index}</span>

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							className={cn(
								'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-sm font-medium text-foreground',
								'hover:bg-muted'
							)}>
							<TypeIcon size={14} className="text-brand" />
							{t(meta.labelKey)}
							<ChevronDown size={14} className="text-foreground-muted" />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						{ACTION_TYPE_META.map(m => {
							const ItemIcon = m.icon;
							return (
								<DropdownMenuItem key={m.type} onSelect={() => onTypeChange(m.type)}>
									<ItemIcon size={14} className="text-brand" />
									{t(m.labelKey)}
								</DropdownMenuItem>
							);
						})}
					</DropdownMenuContent>
				</DropdownMenu>

				<div className="flex-1" />

				<ToggleMini label={t('promotion.action.combine')} checked={combineWithOthers} onCheckedChange={onCombineChange} />

				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button type="button" className="rounded-md p-1 text-foreground-muted hover:bg-muted hover:text-foreground" aria-label="more">
							<MoreVertical size={16} />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						{onDuplicate && (
							<DropdownMenuItem onSelect={onDuplicate}>
								<Copy size={14} /> {t('promotion.action.duplicateAction')}
							</DropdownMenuItem>
						)}
						<DropdownMenuSeparator />
						<DropdownMenuItem variant="destructive" onSelect={onRemove}>
							<Trash2 size={14} /> {t('promotion.action.removeAction')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			<div className="p-4">{children}</div>
		</div>
	);
}
